import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { Eyebrow } from "@/components/shared/Eyebrow";
import { Reveal } from "@/components/shared/Reveal";

const STEPS = [
  { n: "01", t: "Keşif & Analiz", d: "Saha incelemesi, ihtiyaç analizi ve fizibilite." },
  { n: "02", t: "Projelendirme", d: "Yük hesapları, şemalar ve teknik dokümantasyon." },
  { n: "03", t: "Mühendislik & Tasarım", d: "Elektrik ve otomasyon mimarisinin detaylı tasarımı." },
  { n: "04", t: "Uygulama", d: "Kalite ve iş güvenliği standartlarında saha uygulaması." },
  { n: "05", t: "Test & Devreye Alma", d: "Kabul testleri ve güvenli devreye alma." },
  { n: "06", t: "Teknik Destek", d: "Planlı bakım ve satış sonrası teknik destek." },
];

export function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 75%", "end 65%"] });
  const width = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const [active, setActive] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(STEPS.length - 1, Math.floor(v * STEPS.length + 0.15)));
  });

  return (
    <section ref={ref} data-testid="process-section" className="relative bg-light py-24 md:py-28 overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-[0.5] pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow className="mb-6">PROJELERE NASIL YAKLAŞIYORUZ?</Eyebrow>
            <Reveal>
              <h2 className="font-head font-extrabold text-ink tracking-tight text-4xl md:text-5xl max-w-2xl leading-[1.05]">
                Disiplinli bir mühendislik süreci.
              </h2>
            </Reveal>
          </div>
          <span className="hidden md:block font-head font-bold text-ink/15 text-7xl tabular-nums leading-none">
            {STEPS[active].n}
          </span>
        </div>

        {/* Desktop horizontal timeline */}
        <div className="hidden lg:block relative pt-6">
          <div className="absolute top-[30px] left-0 right-0 h-px bg-ink/10" />
          <motion.div className="absolute top-[30px] left-0 h-[2px] bg-cyan" style={{ width }} />
          <div className="grid grid-cols-6">
            {STEPS.map((s, i) => {
              const on = i <= active;
              return (
                <div key={s.n} data-testid={`process-step-${i}`} className="relative pr-6">
                  <div
                    className={`relative z-10 w-3.5 h-3.5 rounded-full transition-all duration-500 mb-9 ${
                      on ? "bg-cyan ring-4 ring-cyan/15" : "bg-white border border-ink/20"
                    }`}
                  />
                  <span className={`font-head font-bold text-sm tabular-nums transition-colors duration-500 ${on ? "text-cyan" : "text-ink/30"}`}>
                    {s.n}
                  </span>
                  <h3 className={`font-head font-bold text-lg mt-2 transition-colors duration-500 ${on ? "text-ink" : "text-ink/40"}`}>
                    {s.t}
                  </h3>
                  <p className={`text-sm mt-2 leading-relaxed transition-colors duration-500 ${on ? "text-ink/55" : "text-ink/30"}`}>
                    {s.d}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile vertical timeline */}
        <div className="lg:hidden relative pl-8 mt-6">
          <div className="absolute left-[5px] top-2 bottom-2 w-px bg-ink/10" />
          <motion.div className="absolute left-[5px] top-2 w-[2px] bg-cyan" style={{ height }} />
          <div className="space-y-9">
            {STEPS.map((s, i) => (
              <div key={s.n} data-testid={`process-step-m-${i}`} className="relative">
                <div className={`absolute -left-8 top-1.5 w-3 h-3 rounded-full transition-colors duration-500 ${i <= active ? "bg-cyan ring-4 ring-cyan/15" : "bg-white border border-ink/20"}`} />
                <span className="font-head text-cyan font-bold text-sm tabular-nums">{s.n}</span>
                <h3 className="font-head font-bold text-ink text-xl mt-1">{s.t}</h3>
                <p className="text-ink/55 text-sm mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
